/**
 * DiscoveryGate -- a tiny pending-state registry shared by an OAuth-populated
 * form (the Add-Connector wizard or the connector detail config editor) and its
 * submit button.
 *
 * Why this exists (#34): an `OAUTH_POPULATED_DROPDOWN` field with `auto_select`
 * is headless -- after OAuth it asynchronously discovers account IDs and writes
 * the result into form state. Between OAuth completing and that fetch resolving,
 * the field's value is empty. If the user clicks Continue/Save in that window,
 * the wizard advances to the Test step (or the editor PATCHes settings) with the
 * field missing, which surfaces as a spurious "Connection Failed" / missing
 * credential. Required-field validation can't catch it: Bing Ads' `customer_ids`
 * is `required: False`.
 *
 * Each `OAuthPopulatedDropdownField` reports whether it is still discovering via
 * `useReportDiscoveryPending`; the submit button reads the aggregate via
 * `useDiscoveryPending` and disables itself while anything is pending.
 *
 * Two SEPARATE contexts on purpose:
 *   - the dispatch context holds the STABLE `setPending` callback (its identity
 *     never changes), so a reporter's effect runs only when its own key/pending
 *     change -- not on every registry mutation. Folding both into one context
 *     value made the reporter effect depend on the changing set, which looped
 *     (cleanup→false, re-run→true, set churns, context identity churns, repeat).
 *   - the pending context holds the changing `Set`, read only by submit buttons.
 *
 * Both hooks are no-ops / safe defaults when no `DiscoveryGateProvider` is
 * mounted above them, so the field and the SaveBar still render in isolation
 * (existing unit tests, any other call site) without a provider.
 *
 * A SECOND, independent registry rides along (#1013): `blocked`. Where `pending`
 * means "this field is still discovering, wait", `blocked` means "this field
 * holds an invalid value, the user must act". `report_suite_ids` is
 * `required` + `multi` with no `auto_select`, so an empty selection is invalid
 * -- but its schema `required` rule was never enforced anywhere (the wizard's
 * zod builder maps every oauth_populated_dropdown to `z.any().optional()`, and
 * while the detail config editor does now run zod over the fields a save sends
 * (#1163), that builder entry still yields no issue for this field type -- so
 * neither surface can enforce it via zod), so an empty list PATCHed cleanly and
 * only failed at run time inside the adapter.
 *
 * Kept separate from `pending` rather than folded into it because the submit
 * buttons render "Discovering accounts…" off the pending set -- the wrong copy
 * for an invalid field. The user-facing explanation for a block lives inline on
 * the field itself; the buttons only need to know they must stay disabled.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

type SetPending = (key: string, pending: boolean) => void;

const DiscoveryGateDispatchContext = createContext<SetPending | null>(null);
const DiscoveryGatePendingContext = createContext<ReadonlySet<string>>(
  new Set<string>(),
);
const DiscoveryGateBlockedDispatchContext = createContext<SetPending | null>(null);
const DiscoveryGateBlockedContext = createContext<ReadonlySet<string>>(
  new Set<string>(),
);

/** Shared key-set reducer used by both registries. */
function useKeySet(): [Set<string>, SetPending] {
  const [keys, setKeys] = useState<Set<string>>(() => new Set());

  // Stable for the provider's lifetime -- reporters depend on this, so their
  // effects never re-run just because the registry mutated.
  const set = useCallback<SetPending>((key, on) => {
    setKeys((prev) => {
      // Avoid allocating a new Set (and re-rendering consumers) when nothing
      // actually changes -- the field re-reports on every render.
      if (on === prev.has(key)) return prev;
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  return [keys, set];
}

export function DiscoveryGateProvider({ children }: { children: ReactNode }) {
  const [pendingKeys, setPending] = useKeySet();
  const [blockedKeys, setBlocked] = useKeySet();

  return (
    <DiscoveryGateDispatchContext.Provider value={setPending}>
      <DiscoveryGatePendingContext.Provider value={pendingKeys}>
        <DiscoveryGateBlockedDispatchContext.Provider value={setBlocked}>
          <DiscoveryGateBlockedContext.Provider value={blockedKeys}>
            {children}
          </DiscoveryGateBlockedContext.Provider>
        </DiscoveryGateBlockedDispatchContext.Provider>
      </DiscoveryGatePendingContext.Provider>
    </DiscoveryGateDispatchContext.Provider>
  );
}

/**
 * Report this field's discovery-pending state to the surrounding gate. The key
 * must be unique within the provider (a field's `name` is unique within one
 * form). Clears the key on unmount. No-op when there is no provider above.
 */
export function useReportDiscoveryPending(key: string, pending: boolean) {
  const setPending = useContext(DiscoveryGateDispatchContext);
  useEffect(() => {
    if (!setPending) return;
    setPending(key, pending);
    return () => setPending(key, false);
  }, [setPending, key, pending]);
}

/**
 * Read the aggregate discovery-pending state. Returns `isAnyPending: false`
 * when there is no provider above, so submit buttons that opt into the gate
 * stay enabled outside a gated form.
 */
export function useDiscoveryPending(): {
  isAnyPending: boolean;
  pendingCount: number;
} {
  const pendingKeys = useContext(DiscoveryGatePendingContext);
  const pendingCount = pendingKeys.size;
  return { isAnyPending: pendingCount > 0, pendingCount };
}

/**
 * Report that this field currently holds an invalid value and submit must stay
 * disabled until the user fixes it (#1013). Independent of the discovery-pending
 * registry above: a field can be blocked without being pending, and the submit
 * buttons' "Discovering accounts…" copy is keyed to pending only. Clears the key
 * on unmount. No-op when there is no provider above.
 */
export function useReportSubmitBlocked(key: string, blocked: boolean) {
  const setBlocked = useContext(DiscoveryGateBlockedDispatchContext);
  useEffect(() => {
    if (!setBlocked) return;
    setBlocked(key, blocked);
    return () => setBlocked(key, false);
  }, [setBlocked, key, blocked]);
}

/**
 * Read the aggregate submit-blocked state. Returns `isAnyBlocked: false` when
 * there is no provider above, so submit buttons that opt into the gate stay
 * enabled outside a gated form.
 */
export function useSubmitBlocked(): {
  isAnyBlocked: boolean;
  blockedCount: number;
} {
  const blockedKeys = useContext(DiscoveryGateBlockedContext);
  const blockedCount = blockedKeys.size;
  return { isAnyBlocked: blockedCount > 0, blockedCount };
}
